/**
 * Timeline cronologica delle attività commerciali del progetto (CRM mini).
 * Mostra chiamate, email, riunioni, sopralluoghi, note, promemoria futuri.
 * Click "Aggiungi attività" apre dialog con form completo.
 */
import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Phone, Mail, Users as UsersIcon, MapPin, StickyNote, Bell, Plus, Trash2,
  CheckCircle2, Calendar as CalendarIcon, FileText, MessageCircle, Pencil,
} from 'lucide-react';
import {
  useProjectActivities, type ProjectActivityType, type ProjectActivity,
} from '@/hooks/useProjectActivities';
import { useEstimates } from '@/hooks/useEstimates';

interface ProjectActivityTimelineProps {
  projectId: string;
}

const TYPE_META: Record<ProjectActivityType, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  call:       { label: 'Chiamata',    icon: <Phone className="h-4 w-4" />,         color: 'text-blue-700',    bg: 'bg-blue-100' },
  email:      { label: 'Email',       icon: <Mail className="h-4 w-4" />,          color: 'text-indigo-700',  bg: 'bg-indigo-100' },
  meeting:    { label: 'Riunione',    icon: <UsersIcon className="h-4 w-4" />,     color: 'text-purple-700',  bg: 'bg-purple-100' },
  site_visit: { label: 'Sopralluogo', icon: <MapPin className="h-4 w-4" />,        color: 'text-emerald-700', bg: 'bg-emerald-100' },
  note:       { label: 'Nota',        icon: <StickyNote className="h-4 w-4" />,    color: 'text-slate-700',   bg: 'bg-slate-100' },
  reminder:   { label: 'Promemoria',  icon: <Bell className="h-4 w-4" />,          color: 'text-amber-700',   bg: 'bg-amber-100' },
};

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const future = ms < 0;
  const abs = Math.abs(ms);
  const sec = Math.floor(abs / 1000);
  const min = Math.floor(sec / 60);
  const hr  = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (day > 7) return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  if (day >= 1) return future ? `fra ${day}g` : `${day}g fa`;
  if (hr >= 1)  return future ? `fra ${hr}h`  : `${hr}h fa`;
  if (min >= 1) return future ? `fra ${min}min` : `${min}min fa`;
  return future ? 'fra poco' : 'or ora';
}

function formatFullDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** Converte un Date in stringa per <input type="datetime-local">. */
function toDatetimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const ProjectActivityTimeline: React.FC<ProjectActivityTimelineProps> = ({ projectId }) => {
  const {
    activities, isLoading,
    createActivity, isCreating,
    updateActivity, isUpdating,
    deleteActivity, completeReminder,
  } = useProjectActivities(projectId);
  // Preventivi del progetto per linkare l'attività (opzionale)
  const { estimates } = useEstimates(projectId);

  const [dialogOpen, setDialogOpen] = useState(false);
  /** Id dell'attività in modifica; null = modalità "Nuova attività". */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<{
    type: ProjectActivityType;
    subject: string;
    body: string;
    contactAt: string;
    /** true = già fatto (contact_done_at = contact_at), false = da fare (contact_done_at = null) */
    contactDone: boolean;
    followUpAt: string;
    linkedEstimateId: string;
  }>({
    type: 'call',
    subject: '',
    body: '',
    contactAt: toDatetimeLocal(new Date()),
    contactDone: true,
    followUpAt: '',
    linkedEstimateId: '',
  });

  const isReminder = form.type === 'reminder';
  const isSubmitting = isCreating || isUpdating;

  /** Separa "Da fare" (almeno una task pendente) dallo storico (tutto completato). */
  const { upcomingReminders, history } = useMemo(() => {
    const upcoming: ProjectActivity[] = [];
    const hist: ProjectActivity[] = [];
    for (const a of activities) {
      const contactPending = !!a.contact_at && !a.contact_done_at;
      const followUpPending = !!a.follow_up_at && !a.completed_at;
      if (contactPending || followUpPending) upcoming.push(a);
      else hist.push(a);
    }
    // Reminder upcoming ordinati per scadenza più vicina
    upcoming.sort((a, b) => new Date(a.follow_up_at!).getTime() - new Date(b.follow_up_at!).getTime());
    // Storico ordinato dal più recente
    hist.sort((a, b) => {
      const ta = new Date(a.contact_at ?? a.created_at).getTime();
      const tb = new Date(b.contact_at ?? b.created_at).getTime();
      return tb - ta;
    });
    return { upcomingReminders: upcoming, history: hist };
  }, [activities]);

  const openDialog = (presetType?: ProjectActivityType) => {
    setEditingId(null);
    setForm({
      type: presetType ?? 'call',
      subject: '',
      body: '',
      contactAt: toDatetimeLocal(new Date()),
      contactDone: true, // default: appena registrato di solito è "già fatto"
      followUpAt: '',
      linkedEstimateId: '',
    });
    setDialogOpen(true);
  };

  /** Apre il dialog in modalità "Modifica" precompilando i campi dall'activity esistente. */
  const openEditDialog = (activity: ProjectActivity) => {
    setEditingId(activity.id);
    setForm({
      type: activity.type,
      subject: activity.subject,
      body: activity.body ?? '',
      contactAt: activity.contact_at
        ? toDatetimeLocal(new Date(activity.contact_at))
        : toDatetimeLocal(new Date()),
      contactDone: !!activity.contact_done_at,
      followUpAt: activity.follow_up_at
        ? toDatetimeLocal(new Date(activity.follow_up_at))
        : '',
      linkedEstimateId: activity.linked_estimate_id ?? '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!form.subject.trim()) return;
    const contactAtIso = isReminder ? null : new Date(form.contactAt).toISOString();
    // contact_done_at: se l'utente ha flaggato "Già fatto", coincide con contactAt;
    // altrimenti resta null (task pendente da chiudere dall'agenda).
    const contactDoneAtIso = !isReminder && form.contactDone && contactAtIso ? contactAtIso : null;
    const payload = {
      type: form.type,
      subject: form.subject,
      body: form.body || null,
      contact_at: contactAtIso,
      contact_done_at: contactDoneAtIso,
      follow_up_at: form.followUpAt ? new Date(form.followUpAt).toISOString() : null,
      linked_estimate_id: form.linkedEstimateId || null,
    };
    if (editingId) {
      updateActivity({ id: editingId, ...payload });
    } else {
      createActivity({ project_id: projectId, ...payload });
    }
    setDialogOpen(false);
  };

  /** Default smart: se la data è in passato → "Già fatto", se è in futuro → "Da fare". */
  const setContactAtSmart = (newValue: string) => {
    const isPast = new Date(newValue).getTime() <= Date.now();
    setForm(f => ({ ...f, contactAt: newValue, contactDone: isPast }));
  };

  return (
    <Card className="animate-fade-in">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-primary" />
              Storico contatti & promemoria
            </CardTitle>
            <CardDescription>
              Tieni traccia di chiamate, email, sopralluoghi e promemoria di follow-up
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => openDialog()}>
            <Plus className="h-4 w-4 mr-1.5" />
            Aggiungi attività
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground text-center py-6">Caricamento…</p>
        ) : activities.length === 0 ? (
          <div className="text-center py-8 border border-dashed rounded-lg space-y-3">
            <Bell className="h-10 w-10 mx-auto text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Nessuna attività ancora registrata</p>
            <div className="flex justify-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => openDialog('call')}>
                <Phone className="h-3.5 w-3.5 mr-1" /> Chiamata
              </Button>
              <Button variant="outline" size="sm" onClick={() => openDialog('site_visit')}>
                <MapPin className="h-3.5 w-3.5 mr-1" /> Sopralluogo
              </Button>
              <Button variant="outline" size="sm" onClick={() => openDialog('reminder')}>
                <Bell className="h-3.5 w-3.5 mr-1" /> Promemoria
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Sezione promemoria futuri (sticky in alto se presenti) */}
            {upcomingReminders.length > 0 && (
              <div className="space-y-2">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 flex items-center gap-1">
                  <Bell className="h-3 w-3" />
                  Da fare ({upcomingReminders.length})
                </div>
                {upcomingReminders.map(a => (
                  <ActivityRow
                    key={a.id}
                    activity={a}
                    isUpcoming
                    onDelete={() => deleteActivity(a.id)}
                    onEdit={() => openEditDialog(a)}
                    onComplete={() => completeReminder(a.id)}
                  />
                ))}
              </div>
            )}

            {/* Storico cronologico */}
            {history.length > 0 && (
              <div className="space-y-2">
                {upcomingReminders.length > 0 && (
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Storico
                  </div>
                )}
                {history.map(a => (
                  <ActivityRow
                    key={a.id}
                    activity={a}
                    onDelete={() => deleteActivity(a.id)}
                    onEdit={() => openEditDialog(a)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Dialog "Aggiungi attività" */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica attività' : 'Nuova attività commerciale'}</DialogTitle>
            <DialogDescription>
              {editingId
                ? 'Aggiorna i campi dell\'attività selezionata.'
                : 'Registra un contatto col cliente o un promemoria di follow-up.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Tipo */}
            <div className="space-y-1.5">
              <Label>Tipo attività</Label>
              <div className="grid grid-cols-3 gap-2">
                {(Object.keys(TYPE_META) as ProjectActivityType[]).map(t => {
                  const meta = TYPE_META[t];
                  const active = form.type === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, type: t }))}
                      className={
                        'inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm transition-colors ' +
                        (active
                          ? `${meta.bg} ${meta.color} border-current font-medium`
                          : 'bg-background hover:bg-muted/50 border-border text-muted-foreground')
                      }
                    >
                      {meta.icon}
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Soggetto */}
            <div className="space-y-1.5">
              <Label htmlFor="act-subject">Oggetto *</Label>
              <Input
                id="act-subject"
                value={form.subject}
                onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))}
                placeholder={
                  form.type === 'call' ? 'es. Conferma sopralluogo settimana prossima'
                  : form.type === 'email' ? 'es. Invio preventivo v2'
                  : form.type === 'meeting' ? 'es. Riunione tecnica in cantiere'
                  : form.type === 'site_visit' ? 'es. Misurazione pareti zona uffici'
                  : form.type === 'reminder' ? 'es. Richiamare cliente per esito'
                  : 'es. Cliente preferisce isolante minerale'
                }
                autoFocus
              />
            </div>

            {/* Data: contact_at per storico, follow_up_at per reminder, opzionalmente entrambi */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {!isReminder && (
                <div className="space-y-1.5">
                  <Label htmlFor="act-when">Quando</Label>
                  <Input
                    id="act-when"
                    type="datetime-local"
                    value={form.contactAt}
                    onChange={(e) => setContactAtSmart(e.target.value)}
                  />
                  <div className="flex items-center gap-2 pt-1">
                    <Switch
                      id="act-contact-done"
                      checked={form.contactDone}
                      onCheckedChange={(v) => setForm(f => ({ ...f, contactDone: v }))}
                    />
                    <Label htmlFor="act-contact-done" className="text-[11px] cursor-pointer text-muted-foreground">
                      {form.contactDone ? 'Già fatto' : 'Da fare'}
                    </Label>
                  </div>
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="act-followup">
                  {isReminder ? 'Quando richiamare *' : 'Promemoria follow-up (opzionale)'}
                </Label>
                <Input
                  id="act-followup"
                  type="datetime-local"
                  value={form.followUpAt}
                  onChange={(e) => setForm(f => ({ ...f, followUpAt: e.target.value }))}
                  min={toDatetimeLocal(new Date())}
                />
                <p className="text-[10px] text-muted-foreground">
                  {form.followUpAt ? 'Sarà mostrato in agenda come "da fare"' : 'Lascia vuoto se non serve un richiamo'}
                </p>
              </div>
            </div>

            {/* Preventivo collegato */}
            {estimates.length > 0 && (
              <div className="space-y-1.5">
                <Label>Preventivo collegato (opzionale)</Label>
                <Select
                  value={form.linkedEstimateId || '__none__'}
                  onValueChange={(v) => setForm(f => ({ ...f, linkedEstimateId: v === '__none__' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Nessuno" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      <span className="text-muted-foreground italic">Nessuno</span>
                    </SelectItem>
                    {estimates.map(e => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name} {e.version > 1 ? `(v${e.version})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Note libere */}
            <div className="space-y-1.5">
              <Label htmlFor="act-body">Dettagli (opzionale)</Label>
              <Textarea
                id="act-body"
                value={form.body}
                onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))}
                placeholder="Cosa avete discusso, prossimi passi, esiti…"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSubmitting}>
              Annulla
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !form.subject.trim() || (isReminder && !form.followUpAt)}
            >
              {isSubmitting
                ? 'Salvataggio…'
                : editingId
                ? 'Salva modifiche'
                : 'Salva attività'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ============================================================
// SUB-COMPONENTS
// ============================================================

interface ActivityRowProps {
  activity: ProjectActivity;
  isUpcoming?: boolean;
  onDelete: () => void;
  onEdit?: () => void;
  onComplete?: () => void;
}

const ActivityRow: React.FC<ActivityRowProps> = ({ activity: a, isUpcoming, onDelete, onEdit, onComplete }) => {
  const meta = TYPE_META[a.type];
  const dateIso = isUpcoming ? a.follow_up_at : (a.contact_at ?? a.created_at);
  return (
    <div className={
      'flex items-start gap-3 p-3 rounded-lg border transition-colors group ' +
      (isUpcoming ? 'bg-amber-50/50 border-amber-200' : 'hover:bg-muted/30')
    }>
      <div className={`p-1.5 rounded-full ${meta.bg} ${meta.color} shrink-0 mt-0.5`}>
        {meta.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-semibold uppercase tracking-wide ${meta.color}`}>
            {meta.label}
          </span>
          <span className="text-xs text-muted-foreground" title={formatFullDate(dateIso)}>
            {formatRelative(dateIso)}
          </span>
          {a.estimates && (
            <Badge variant="outline" className="text-[10px] h-4 px-1 gap-0.5 bg-blue-50 text-blue-700 border-blue-200">
              <FileText className="h-2.5 w-2.5" />
              {a.estimates.name}{a.estimates.version > 1 ? ` v${a.estimates.version}` : ''}
            </Badge>
          )}
        </div>
        <div className="font-medium text-sm mt-0.5 truncate">{a.subject}</div>
        {a.body && (
          <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-3">{a.body}</div>
        )}
        {!isUpcoming && a.follow_up_at && (
          <div className="text-[11px] text-amber-700 mt-1 inline-flex items-center gap-1">
            <CalendarIcon className="h-3 w-3" />
            Follow-up programmato: {formatFullDate(a.follow_up_at)}
          </div>
        )}
      </div>
      <div className="shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        {isUpcoming && onComplete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-emerald-600 hover:bg-emerald-50"
            onClick={onComplete}
            title="Segna come fatto"
          >
            <CheckCircle2 className="h-4 w-4" />
          </Button>
        )}
        {onEdit && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onEdit}
            title="Modifica attività"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive hover:bg-destructive/10"
          onClick={() => {
            if (confirm(`Eliminare l'attività "${a.subject}"?`)) onDelete();
          }}
          title="Elimina attività"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default ProjectActivityTimeline;
